import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { STREVDA_SLOTS } from '@web-platform/shared-universal-types/src/network/requestTypes';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { importRetryWrapper } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { formatStrevdaVariables } from './formatStrevdaVariables';

import type { StrevdaContext } from '@skyscanner/strevda-gam';
import type { Slot } from '@web-platform/shared-universal-types/src/network/endpoints/strevda';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';

const shimIntoNetworkResponse = (slots: Slot[]) =>
  ({
    error: false,
    headers: {},
    body: slots,
    status: 200,
    type: STREVDA_SLOTS,
  }) as const;

type SlotsData = {
  body: Slot[];
  error: false;
  headers: {};
  status: number;
  type: 'STREVDA_SLOTS';
};

export function request(item: QueryItem): Promise<SlotsData> {
  return new Promise<Slot[]>((resolve) => {
    const showInlineAds = getFeatureDecision(FEATURE_DECISION_KEYS.SHOW_INLINE_ADS);

    if (showInlineAds) {
      const gamAdsInitialDelay = getConfigServiceValue(
        CONFIG_SERVICE_KEYS.OPS.GAM_ADS_INITIAL_DELAY,
      );

      setTimeout(() => {
        // Google Ads Manager integration
        importRetryWrapper(
          () =>
            import(
              /* webpackChunkName: "StrevdaGam" */
              '@skyscanner/strevda-gam'
            ),
        )
          // eslint-disable-next-line @typescript-eslint/consistent-type-imports
          .then(async (strevdaModule: typeof import('@skyscanner/strevda-gam')) => {
            const { DEFAULT_SLOT_REQUEST_TIMEOUT, compatibilityUtils, gamService } = strevdaModule;

            const showGoogleAds = getFeatureDecision(FEATURE_DECISION_KEYS.SHOW_GOOGLE_INLINE_ADS);

            if (showGoogleAds) {
              // init strevda
              // https://github.skyscannertools.net/adverts/strevda-gam/tree/master#through-bootstrap
              try {
                const { device, pageName } = item.variables;
                const updatedVariables = formatStrevdaVariables(item.variables);
                const platform =
                  device &&
                  typeof device === 'object' &&
                  'isMobilePhone' in device &&
                  device.isMobilePhone
                    ? 'mobile'
                    : 'desktop';

                const slotRequestData = compatibilityUtils.buildSlotSetup(
                  'inline-slot-1',
                  `${platform}.${String(pageName)}/inline`,
                  'flightsInline',
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  updatedVariables as StrevdaContext,
                );
                const slotData = await gamService.requestAdsFromGAM(
                  [slotRequestData],
                  { slotRequestTimeout: DEFAULT_SLOT_REQUEST_TIMEOUT },
                  { enabled: false },
                  false,
                );
                resolve(
                  slotData.map((advert) => ({
                    position: 1,
                    advert,
                  })),
                );
              } catch (err) {
                logWarn({
                  component: 'strevda-slots-client',
                  error: err,
                  message: 'Error initialising strevda, GAM ads will not show',
                });
              }
            }
          })
          .catch((err) => {
            logWarn({
              component: 'strevda-slots-client',
              error: err,
              message: 'Failed to load the strevda chunk, GAM ads will not show',
            });
          });
      }, gamAdsInitialDelay);
    }
  }).then((slots) => shimIntoNetworkResponse(slots));
}
